import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useIntelSources } from '../hooks/useIntelSources';
import { securityApi } from '../services/securityApi';
import type { IntelSource } from '../types/security';
import { AddSourceModal } from './AddSourceModal';
import { EmptyState, ErrorState, LoadingSkeleton } from './LoadingSkeleton';
import { SourceDeleteConfirm } from './SourceDeleteConfirm';
import { SourceEditModal } from './SourceEditModal';

function SourceCard({
  source,
  onEdit,
  onDelete,
  onReactivate,
  reactivating,
}: {
  source: IntelSource;
  onEdit: () => void;
  onDelete: () => void;
  onReactivate: () => void;
  reactivating: boolean;
}): React.JSX.Element {
  return (
    <div
      style={{
        padding: '1rem',
        border: '1px solid var(--border-default, #e5e7eb)',
        borderRadius: '0.5rem',
        background: 'var(--bg-secondary)',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.375rem',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong style={{ fontFamily: 'monospace' }}>{source.name}</strong>
        <span
          style={{
            fontSize: '0.75rem',
            padding: '0.125rem 0.5rem',
            borderRadius: '9999px',
            background: source.is_active
              ? 'var(--severity-low-bg, #eff6ff)'
              : 'var(--severity-info-bg, #f3f4f6)',
            color: source.is_active
              ? 'var(--severity-low-text, #1e40af)'
              : 'var(--severity-info-text, #374151)',
          }}
        >
          {source.is_active ? 'active' : 'inactive'}
        </span>
      </div>
      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
        {source.source_category} · {source.schedule_interval ?? '—'}
      </div>
      {source.source_url && (
        <div
          style={{
            fontSize: '0.75rem',
            color: 'var(--text-secondary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={source.source_url}
        >
          {source.source_url}
        </div>
      )}
      <div style={{ fontSize: '0.75rem' }}>
        Last polled:{' '}
        {source.last_checked_at
          ? new Date(source.last_checked_at).toLocaleString()
          : 'never'}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
        <button
          className="btn btn-secondary"
          onClick={onEdit}
          aria-label={`Edit ${source.name}`}
        >
          Edit
        </button>
        {/* WQ-286 Item 3: Reactivate visible only on inactive sources. */}
        {!source.is_active && (
          <button
            className="btn btn-primary"
            onClick={onReactivate}
            disabled={reactivating}
            aria-disabled={reactivating}
            aria-label={`Reactivate ${source.name}`}
            title="Reverse the soft-delete; flips is_active=true so dispatcher cycles pick this source up again."
          >
            {reactivating ? 'Reactivating…' : 'Reactivate'}
          </button>
        )}
        <button
          className="btn btn-secondary"
          onClick={onDelete}
          aria-label={`Delete ${source.name}`}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

type ActiveFilter = 'all' | 'active' | 'inactive';

export function SourcesTab(): React.JSX.Element {
  const { authFetch } = useAuth();
  const { data, isLoading, error, refetch } = useIntelSources();
  const [editingSource, setEditingSource] = useState<IntelSource | null>(null);
  const [deletingSource, setDeletingSource] = useState<IntelSource | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('active');
  const [reactivatingId, setReactivatingId] = useState<string | null>(null);
  const [reactivateError, setReactivateError] = useState<string | null>(null);

  // WQ-286 Item 3: reactivate flow. No confirm modal — reactivation is
  // lower-consequence than dismiss-fp or hard-delete; single click +
  // optimistic refetch.
  const reactivateSource = async (source: IntelSource) => {
    setReactivateError(null);
    setReactivatingId(source.id);
    try {
      await securityApi.reactivateIntelSource(authFetch, source.id);
      refetch();
    } catch (e) {
      setReactivateError(
        `Reactivate failed for ${source.name}: ${e instanceof Error ? e.message : String(e)}`
      );
    } finally {
      setReactivatingId(null);
    }
  };

  const filtered = (data ?? []).filter((s) => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'active') return s.is_active;
    return !s.is_active;
  });

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '0.75rem',
          flexWrap: 'wrap',
        }}
      >
        <div role="group" aria-label="Filter by active state" style={{ display: 'flex', gap: '0.25rem' }}>
          {(['active', 'all', 'inactive'] as ActiveFilter[]).map((f) => (
            <button
              key={f}
              className={`btn ${activeFilter === f ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setActiveFilter(f)}
              aria-pressed={activeFilter === f}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
            >
              {f === 'all' ? 'All' : f === 'active' ? 'Active only' : 'Inactive only'}
            </button>
          ))}
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowAddModal(true)}
        >
          + Add source
        </button>
      </div>

      {isLoading && !data && <LoadingSkeleton />}
      {error && <ErrorState error={error} onRetry={refetch} />}
      {!isLoading && data && filtered.length === 0 && (
        <EmptyState
          message={
            activeFilter === 'all'
              ? 'No intel sources registered. cyber-agent ships with aws-alas, nvd, osv, ghsa, cisa-kev — these should be visible. Check observed_at on the source rows; if stale, verify dispatcher cycles.'
              : `No ${activeFilter} sources. Adjust the filter above to see other rows.`
          }
        />
      )}

      {data && filtered.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: '0.75rem',
          }}
        >
          {filtered.map((source) => (
            <SourceCard
              key={source.id}
              source={source}
              onEdit={() => setEditingSource(source)}
              onDelete={() => setDeletingSource(source)}
              onReactivate={() => reactivateSource(source)}
              reactivating={reactivatingId === source.id}
            />
          ))}
        </div>
      )}

      {reactivateError && (
        <div
          role="alert"
          style={{
            marginTop: '0.75rem',
            color: 'var(--severity-critical-text, #991b1b)',
          }}
        >
          {reactivateError}
        </div>
      )}

      {editingSource && (
        <SourceEditModal
          source={editingSource}
          onClose={() => setEditingSource(null)}
          onSaved={() => {
            setEditingSource(null);
            refetch();
          }}
        />
      )}

      {deletingSource && (
        <SourceDeleteConfirm
          source={deletingSource}
          onClose={() => setDeletingSource(null)}
          onDeleted={() => {
            setDeletingSource(null);
            refetch();
          }}
        />
      )}

      {showAddModal && (
        <AddSourceModal
          onClose={() => setShowAddModal(false)}
          onCreated={() => {
            setShowAddModal(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}
